import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { jmd } from "@/lib/format";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from "recharts";
import { DollarSign, ShoppingCart, Package, TrendingUp, Plus, Loader2, LayoutGrid, Boxes, Star, AlertTriangle, Sparkles, Truck } from "lucide-react";
import { toast } from "sonner";
import { MerchantStatements } from "@/components/MerchantStatements";
import { MerchantDisputes } from "@/components/DisputeForm";
import { MerchantDispatch } from "@/components/haulage/MerchantDispatch";

const REV_DATA = [
  { m: "Feb", v: 420000 }, { m: "Mar", v: 510000 }, { m: "Apr", v: 480000 },
  { m: "May", v: 640000 }, { m: "Jun", v: 720000 }, { m: "Jul", v: 810000 },
];

export default function Seller() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [ov, setOv] = useState(null);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ name: "", category: "hardware", price: "", prev_price: "", stock: 10, description: "", parish: "Kingston", image: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const generateAI = async () => {
    if (!form.name) { toast.error("Enter a product name first"); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post("/merchant/ai-assist", { name: form.name, category: form.category, notes: form.description });
      const specs = (data.specs || []).length ? "\n\nKey features:\n" + data.specs.map((s) => `\u2022 ${s}`).join("\n") : "";
      setForm((f) => ({ ...f, description: (data.description || "") + specs, price: f.price || data.suggested_price_jmd || "" }));
      toast.success(`AI drafted your listing${data.suggested_price_jmd ? ` \u00b7 suggested J$${data.suggested_price_jmd}` : ""}`);
    } catch { toast.error("Merchant AI is unavailable right now"); }
    setAiLoading(false);
  };

  useEffect(() => { if (user === false) navigate("/login"); else if (user && !["merchant", "admin"].includes(user.role)) navigate("/account"); }, [user]);
  const load = () => { api.get("/merchant/overview").then(({ data }) => setOv(data)).catch(() => {}); api.get("/merchant/products").then(({ data }) => setProducts(data)).catch(() => {}); };
  useEffect(() => { if (user && ["merchant", "admin"].includes(user.role)) load(); }, [user]);

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: `${process.env.REACT_APP_BACKEND_URL}${data.url}` }));
      toast.success("Photo uploaded");
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const addProduct = async (e) => {
    e.preventDefault();
    if (!form.name || !form.price) { toast.error("Name and price required"); return; }
    setSaving(true);
    try {
      await api.post("/merchant/products", { ...form, price: Number(form.price), prev_price: Number(form.prev_price) || 0, stock: Number(form.stock) });
      toast.success("Product added"); setForm({ name: "", category: "hardware", price: "", prev_price: "", stock: 10, description: "", parish: "Kingston", image: "" }); load(); setTab("products");
    } catch { toast.error("Could not add product"); } finally { setSaving(false); }
  };

  if (!ov) return <div className="grid place-items-center py-40"><Loader2 className="animate-spin text-emerald" size={32} /></div>;

  const TABS = [["overview", "Overview", LayoutGrid], ["products", "Products", Boxes], ["add", "Add Product", Plus], ["analytics", "Analytics", TrendingUp], ["statements", "Statements", DollarSign], ["disputes", "Disputes", AlertTriangle], ["dispatch", "Dispatch", Truck]];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-display text-3xl font-bold">Merchant Dashboard</h1><p className="text-muted-foreground capitalize">{ov.merchant_slug.replace(/-/g, " ")}</p></div>
      </div>

      <div className="flex gap-2 mb-6 overflow-x-auto sn-scroll-x" data-testid="dashboard-nav-tabs">
        {TABS.map(([k, l, Ic]) => (
          <button key={k} onClick={() => setTab(k)} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold shrink-0 transition-colors ${tab === k ? "bg-ink text-white" : "bg-card border border-border"}`} data-testid={`tab-${k}`}><Ic size={16} /> {l}</button>
        ))}
      </div>

      {tab === "statements" && <MerchantStatements />}
      {tab === "disputes" && <MerchantDisputes />}
      {tab === "dispatch" && <MerchantDispatch />}
      {tab === "overview" && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[["Revenue", jmd(ov.revenue), DollarSign], ["Orders", ov.orders, ShoppingCart], ["Products", ov.products, Package], ["Avg Order", jmd(ov.aov), TrendingUp]].map(([l, v, Ic]) => (
              <div key={l} className="bg-card rounded-2xl border border-border p-5"><Ic size={20} className="text-emerald mb-2" /><div className="font-display font-bold text-2xl">{v}</div><div className="text-xs text-muted-foreground">{l}</div></div>
            ))}
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-card rounded-2xl border border-border p-6">
              <h3 className="font-display font-bold mb-4">Revenue Trend</h3>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={REV_DATA}><CartesianGrid strokeDasharray="3 3" stroke="#eef2f0" /><XAxis dataKey="m" fontSize={12} /><YAxis fontSize={12} tickFormatter={(v) => `${v / 1000}k`} /><Tooltip formatter={(v) => jmd(v)} /><Line type="monotone" dataKey="v" stroke="#E11B22" strokeWidth={3} dot={{ r: 4 }} /></LineChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-card rounded-2xl border border-border p-6">
              <h3 className="font-display font-bold mb-4 flex items-center gap-2"><AlertTriangle size={18} className="text-gold" /> Low Stock Alerts</h3>
              {ov.low_stock.length === 0 ? <p className="text-sm text-muted-foreground">All products well stocked.</p> : ov.low_stock.map((p) => (
                <div key={p.id} className="flex items-center justify-between py-2 border-b border-border last:border-0"><span className="text-sm line-clamp-1">{p.name}</span><span className="text-sm font-semibold text-coral">{p.stock} left</span></div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "products" && (
        <div className="bg-card rounded-2xl border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-left"><tr><th className="p-4">Product</th><th className="p-4">Price</th><th className="p-4">Stock</th><th className="p-4">Rating</th></tr></thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className="border-t border-border"><td className="p-4 flex items-center gap-3"><img src={p.image} alt="" className="h-10 w-10 rounded-lg object-cover" /><span className="line-clamp-1 max-w-xs">{p.name}</span></td><td className="p-4 font-semibold">{jmd(p.price)}</td><td className="p-4">{p.stock}</td><td className="p-4 flex items-center gap-1"><Star size={13} className="fill-gold text-gold" /> {p.rating}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "add" && (
        <form onSubmit={addProduct} className="bg-card rounded-2xl border border-border p-6 max-w-2xl space-y-4">
          <h3 className="font-display font-bold text-lg">Add New Product</h3>
          <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Product name" className="w-full rounded-xl border border-border px-4 py-2.5 text-sm" data-testid="product-name-input" />
          <button type="button" onClick={generateAI} disabled={aiLoading} className="flex items-center gap-2 bg-emerald/10 text-emerald border border-emerald/30 rounded-xl px-4 py-2 text-sm font-semibold disabled:opacity-60" data-testid="ai-generate-btn">
            {aiLoading ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Generate description & price with AI
          </button>
          <div className="grid sm:grid-cols-2 gap-4">
            <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className="rounded-xl border border-border px-4 py-2.5 text-sm">
              {["electronics", "fashion", "home", "grocery", "hardware", "automotive", "beauty", "jamaica-made", "business"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={form.parish} onChange={(e) => setForm((f) => ({ ...f, parish: e.target.value }))} className="rounded-xl border border-border px-4 py-2.5 text-sm">
              {["Kingston", "Montego Bay", "Portmore", "Spanish Town", "Mandeville", "Ocho Rios", "Negril"].map((p) => <option key={p}>{p}</option>)}
            </select>
            <input value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} placeholder="Price (J$)" type="number" className="rounded-xl border border-border px-4 py-2.5 text-sm" data-testid="product-price-input" />
            <input value={form.prev_price} onChange={(e) => setForm((f) => ({ ...f, prev_price: e.target.value }))} placeholder="Compare-at price (optional)" type="number" className="rounded-xl border border-border px-4 py-2.5 text-sm" />
            <input value={form.stock} onChange={(e) => setForm((f) => ({ ...f, stock: e.target.value }))} placeholder="Stock" type="number" className="rounded-xl border border-border px-4 py-2.5 text-sm" />
          </div>
          <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Description" rows={3} className="w-full rounded-xl border border-border px-4 py-2.5 text-sm" />
          <div>
            <label className="text-sm font-semibold flex items-center gap-2 mb-2"><Boxes size={16} className="text-emerald" /> Product Photo</label>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 border-2 border-dashed border-border rounded-xl px-4 py-3 text-sm cursor-pointer hover:border-emerald transition-colors" data-testid="product-image-upload">
                {uploading ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} {uploading ? "Uploading…" : "Upload image"}
                <input type="file" accept="image/*" onChange={uploadImage} className="hidden" />
              </label>
              {form.image && <img src={form.image} alt="preview" className="h-16 w-16 rounded-lg object-cover border border-border" data-testid="product-image-preview" />}
            </div>
          </div>
          <button disabled={saving} className="bg-ink text-white rounded-xl px-6 py-2.5 font-semibold flex items-center gap-2 disabled:opacity-60" data-testid="product-save-btn">{saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Add Product</button>
        </form>
      )}

      {tab === "analytics" && (
        <div className="bg-card rounded-2xl border border-border p-6">
          <h3 className="font-display font-bold mb-4">Units Sold by Month</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={REV_DATA}><CartesianGrid strokeDasharray="3 3" stroke="#eef2f0" /><XAxis dataKey="m" fontSize={12} /><YAxis fontSize={12} /><Tooltip /><Bar dataKey="v" fill="#0B131F" radius={[8, 8, 0, 0]} /></BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
